import { motion } from 'framer-motion';
import {
  Monitor,
  Database,
  BarChart3,
  Workflow,
  Link2,
  Shield,
  FileSpreadsheet,
  ArrowRight,
  Phone,
  CheckCircle2,
  Settings,
  Target,
  TrendingUp,
  Users
} from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { companyInfo } from '../data';

const services = [
  {
    icon: Database,
    title: 'Enterprise Resource Planning (ERP)',
    description: 'Implementasi & integrasi sistem ERP untuk efisiensi operasional'
  },
  {
    icon: BarChart3,
    title: 'Pembuatan & Pengembangan Dashboard',
    description: 'Data-driven decision-making melalui dashboard visual & interaktif'
  },
  {
    icon: Database,
    title: 'Data Infrastructure & Business Intelligence',
    description: 'Pengelolaan & pemrosesan data untuk analisis bisnis yang lebih akurat'
  },
  {
    icon: Workflow,
    title: 'Automasi Workflow',
    description: 'Digitalisasi & otomatisasi alur kerja untuk meningkatkan efisiensi'
  },
  {
    icon: Link2,
    title: 'Integrasi Sistem & API',
    description: 'Konektivitas antar sistem untuk meningkatkan produktivitas'
  },
  {
    icon: Shield,
    title: 'Monitoring & Evaluasi Digital System',
    description: 'Audit dan optimasi sistem digital perusahaan'
  },
  {
    icon: FileSpreadsheet,
    title: 'Pembuatan Administrative Excel Template',
    description: 'Template manajemen yang efisien untuk operasional harian'
  }
];

const suitableFor = [
  'Membangun & mengembangkan sistem manajemen berbasis data',
  'Meningkatkan efisiensi & produktivitas dengan digital tools',
  'Mengoptimalkan pengelolaan operasional dengan solusi berbasis teknologi',
  'Menjaga daya saing bisnis dengan adopsi teknologi terbaru',
  'Penyesuaian & Perombakan bisnis proses'
];

const workSteps = [
  {
    icon: Settings,
    title: 'Analisis Business Process',
    description: 'Identifikasi dan optimasi alur kerja bisnis'
  },
  {
    icon: Users,
    title: 'Penyusunan SOP & Struktur Organisasi',
    description: 'Standarisasi operasional & efisiensi tim'
  },
  {
    icon: Target,
    title: 'Sistem Manajemen Internal & KPI',
    description: 'Pengukuran kinerja yang efektif'
  },
  {
    icon: BarChart3,
    title: 'Pembuatan Dashboard & Template Manajemen',
    description: 'Monitoring real-time untuk keputusan strategis'
  },
  {
    icon: Shield,
    title: 'Management Risk Analysis & Balance Scorecard',
    description: 'Evaluasi risiko & penyusunan strategi berbasis data'
  },
  {
    icon: TrendingUp,
    title: 'Evaluasi Rencana Operasional (Bulanan)',
    description: 'Monitoring & perbaikan berkala'
  },
  {
    icon: Workflow,
    title: 'Process Improvement & Lean Management',
    description: 'Pengurangan pemborosan & peningkatan efisiensi kerja'
  },
  {
    icon: Database,
    title: 'Strategic Resource Allocation',
    description: 'Optimasi alokasi sumber daya untuk efisiensi operasional'
  }
];

export function BusinessITService() {
  const { ref: heroRef, isInView: heroInView } = useScrollAnimation({ threshold: 0.1 });
  const { ref: servicesRef, isInView: servicesInView } = useScrollAnimation({ threshold: 0.1 });
  const { ref: stepsRef, isInView: stepsInView } = useScrollAnimation({ threshold: 0.1 });

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative pt-32 pb-20 overflow-hidden"
      >
        {/* Video Background - z-0 to stay at back */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover object-center z-0"
        >
          <source src="/erp-solutions-bg.mp4" type="video/mp4" />
        </video>
        
        {/* Dark Navy Blue Overlay */}
        <div className="absolute inset-0 bg-[#0a1628]/85 z-[1]" />

        <div className="relative max-w-7xl mx-auto section-padding z-[2]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-4 mb-4">
              <span className="text-6xl font-display font-bold text-white/10">06</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
              ERP Solutions & Business Automation
            </h1>

            <p className="text-xl text-primary-400 mb-4 leading-relaxed">
              Enterprise Resource Planning & Digital Transformation
            </p>

            <p className="text-white/60 mb-8">
              <strong className="text-primary-400">Optimalkan Efisiensi & Kinerja Operasional Bisnis Anda!</strong>
            </p>

            <motion.a
              href={`tel:${companyInfo.phone}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30"
            >
              <Phone className="w-5 h-5" />
              Konsultasi Gratis
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section ref={servicesRef} className="py-20">
        <div className="max-w-7xl mx-auto section-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Ruang Lingkup Layanan Kami
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="glass rounded-2xl p-6 hover:border-primary-500/30 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-primary-600/20 flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-primary-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{service.title}</h3>
                <p className="text-white/60 text-sm">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Suitable For Section */}
      <section className="py-20 relative overflow-hidden">
        {/* Video Background - z-0 to stay at back */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover object-center z-0"
        >
          <source src="/erp-suitable-bg.mp4" type="video/mp4" />
        </video>
        
        {/* Dark Navy Blue Overlay */}
        <div className="absolute inset-0 bg-[#0a1628]/85 z-[1]" />

        <div className="max-w-7xl mx-auto section-padding relative z-[2]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass rounded-2xl p-8 md:p-12"
          >
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-8 text-center">
              Cocok Dengan Bisnis yang Memiliki Kebutuhan
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              {suitableFor.map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start gap-3 p-4 bg-white/5 rounded-xl"
                >
                  <CheckCircle2 className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                  <span className="text-white/80">{item}</span>
                </motion.div>
              ))}
            </div>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-10 text-center"
            >
              <motion.a
                href={`tel:${companyInfo.phone}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center gap-2 px-8 py-4 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30"
              >
                <Phone className="w-5 h-5" />
                Konsultasi Gratis
                <ArrowRight className="w-5 h-5" />
              </motion.a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Work Steps Section */}
      <section ref={stepsRef} className="py-20">
        <div className="max-w-7xl mx-auto section-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={stepsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Langkah Kerja Kami
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {workSteps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 20 }}
                animate={stepsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass rounded-2xl p-6 text-center"
              >
                <div className="w-12 h-12 rounded-xl bg-primary-600/20 flex items-center justify-center mx-auto mb-4">
                  <step.icon className="w-6 h-6 text-primary-400" />
                </div>
                <span className="text-3xl font-bold text-primary-400 mb-2 block">{String(index + 1).padStart(2, '0')}</span>
                <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                <p className="text-white/60 text-sm">{step.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-12 glass rounded-2xl p-6 text-center"
          >
            <p className="text-white/70 text-lg mb-6">
              <strong className="text-primary-400">SEMUA RUANG LINGKUP PENGERJAAN BERDASARKAN HASIL DISKUSI DENGAN KLIEN</strong>
            </p>
            <motion.a
              href={`tel:${companyInfo.phone}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30"
            >
              <Phone className="w-5 h-5" />
              Konsultasi Gratis
              <ArrowRight className="w-5 h-5" />
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
